import { supabase } from "@/integrations/supabase/client";
import { MedicalKnowledgeEntry } from "./types";

export class MedicalSearchService {
  // Optimized search with simple, fast queries
  static async searchRelevantKnowledge(
    symptoms: string[],
    conditions: string[] = [],
    categories: string[] = [],
    limit: number = 20
  ): Promise<MedicalKnowledgeEntry[]> {
    try {
      // Validate input
      if (!Array.isArray(symptoms) || symptoms.length === 0) {
        console.log('No symptoms provided, skipping database search');
        return [];
      }

      console.log('Optimized medical knowledge search for:', symptoms.slice(0, 2), 'limit:', limit);

      // Ultra-fast query - minimal database load
      let query = supabase
        .from('medical_knowledge_base')
        .select('id, source, external_id, title, summary, evidence_level, medical_category')
        .limit(Math.min(limit, 10)); // Cap at 10 for speed

      // Simple category filter only
      if (categories.length > 0) {
        query = query.in('medical_category', categories);
      }

      // Single ordering for speed
      query = query.order('evidence_level', { ascending: false });

      const { data, error } = await query;

      if (error) {
        console.error('Error searching medical knowledge:', error);
        return [];
      }

      console.log(`Found ${data?.length || 0} medical knowledge entries`);
      
      // Minimal processing - return top results
      return (data || []).slice(0, limit);
    } catch (error) {
      console.error('Error in searchRelevantKnowledge:', error);
      return [];
    }
  }

  // Fast clinical guidelines search
  static async getClinicalGuidelines(condition?: string): Promise<MedicalKnowledgeEntry[]> {
    try {
      let query = supabase
        .from('medical_knowledge_base')
        .select('*')
        .in('source', ['trip', 'clinical_guidelines'])
        .limit(15);

      if (condition) {
        query = query.ilike('title', `%${condition}%`);
      }

      query = query.order('evidence_level', { ascending: false });

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching clinical guidelines:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getClinicalGuidelines:', error);
      return [];
    }
  }

  // Fast drug safety search (includes OpenFDA and DailyMed)
  static async getDrugSafetyInfo(drugNames: string[] = []): Promise<MedicalKnowledgeEntry[]> {
    try {
      let query = supabase
        .from('medical_knowledge_base')
        .select('*')
        .in('source', ['openfda', 'dailymed'])
        .limit(15);

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching drug safety info:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getDrugSafetyInfo:', error);
      return [];
    }
  }

  // Get clinical trials data
  static async getClinicalTrials(condition?: string): Promise<MedicalKnowledgeEntry[]> {
    try {
      let query = supabase
        .from('medical_knowledge_base')
        .select('*')
        .eq('source', 'clinicaltrials')
        .limit(10);

      if (condition) {
        query = query.contains('condition_tags', [condition.toLowerCase()]);
      }

      query = query.order('evidence_level', { ascending: false });

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching clinical trials:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getClinicalTrials:', error);
      return [];
    }
  }
}